import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { ActionTableCellComponent } from "../action-table-component";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import { warehouseCostRateService } from "../../utils/services/warehouseCostRateService";
import { wmsService } from "../../utils/services/wmsProvider";
import CostRateModal from "./cost-rate-modal";

const RATE_KEYS = [
  "inbound_fixed",
  "inbound_per_unit",
  "storage_fixed",
  "storage_per_unit",
  "outbound_fixed",
  "outbound_per_unit",
];

const emptyForm = () => ({
  warehouse_id: "",
  warehouse: null,
  inbound_fixed: "0",
  inbound_per_unit: "0",
  storage_fixed: "0",
  storage_per_unit: "0",
  outbound_fixed: "0",
  outbound_per_unit: "0",
  currency: "USD",
  effective_from: new Date().toISOString().slice(0, 10),
  effective_to: "",
});

const toDayKey = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const addDays = (dateKey, days) => {
  const date = new Date(`${dateKey}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const warehouseIdOf = (rate) => {
  if (!rate) return "";
  if (rate.warehouse && rate.warehouse._id) return String(rate.warehouse._id);
  if (rate.warehouse_id && typeof rate.warehouse_id === "object") {
    return String(rate.warehouse_id._id || "");
  }
  return String(rate.warehouse_id || "");
};

const warehouseLabel = (rate, warehouses) => {
  const warehouse =
    rate?.warehouse ||
    warehouses.find((item) => String(item._id) === warehouseIdOf(rate));
  if (!warehouse) return warehouseIdOf(rate) || "—";
  if (warehouse.name && warehouse.warehouse_code) {
    return `${warehouse.name} (${warehouse.warehouse_code})`;
  }
  return warehouse.name || warehouse.warehouse_code || warehouseIdOf(rate);
};

const formatRate = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const headerCellSx = {
  fontWeight: "600",
  backgroundColor: "var(--table-header-bg)",
  whiteSpace: "nowrap",
};

const CostRateList = ({ isOpened, clientId, disabled }) => {
  const [rates, setRates] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async (signal) => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [rateResponse, warehouseResponse] = await Promise.all([
        warehouseCostRateService.fetch(clientId, 100, 0, signal),
        wmsService.fetch(clientId, 100, 0, signal ? { signal } : undefined),
      ]);
      const nextRates = [...(rateResponse?.data || [])].sort((a, b) => {
        const nameCmp = warehouseLabel(a, warehouseResponse?.data || []).localeCompare(
          warehouseLabel(b, warehouseResponse?.data || []),
        );
        if (nameCmp !== 0) return nameCmp;
        return toDayKey(b.effective_from).localeCompare(toDayKey(a.effective_from));
      });
      setRates(nextRates);
      setWarehouses(warehouseResponse?.data || []);
    } catch (error) {
      console.error("Fetch cost rates failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    if (isOpened) {
      load(controller.signal);
    }
    return () => controller.abort();
  }, [isOpened, clientId]);

  const openCreate = (event) => {
    event?.stopPropagation?.();
    const next = emptyForm();
    if (warehouses.length === 1) {
      next.warehouse_id = warehouses[0]._id;
      next.warehouse = warehouses[0];
    }
    setEditing(null);
    setViewOnly(false);
    setForm(next);
    setModalOpen(true);
  };

  const openEdit = (rate, readOnly = false) => {
    const warehouse =
      rate.warehouse ||
      warehouses.find((item) => String(item._id) === warehouseIdOf(rate)) ||
      null;
    setEditing(rate);
    setViewOnly(readOnly || disabled);
    setForm({
      warehouse_id: warehouseIdOf(rate),
      warehouse,
      inbound_fixed: String(rate.inbound_fixed ?? ""),
      inbound_per_unit: String(rate.inbound_per_unit ?? ""),
      storage_fixed: String(rate.storage_fixed ?? ""),
      storage_per_unit: String(rate.storage_per_unit ?? ""),
      outbound_fixed: String(rate.outbound_fixed ?? ""),
      outbound_per_unit: String(rate.outbound_per_unit ?? ""),
      currency: rate.currency || "USD",
      effective_from: toDayKey(rate.effective_from),
      effective_to: toDayKey(rate.effective_to),
    });
    setModalOpen(true);
  };

  const onChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onWarehouseChange = (warehouse) => {
    setForm((prev) => ({
      ...prev,
      warehouse,
      warehouse_id: warehouse?._id || "",
    }));
  };

  const closeOpenEndedCardIfNeeded = async () => {
    if (editing) return null;
    const from = form.effective_from;
    const to = form.effective_to || null;
    const openCard = rates.find((rate) => {
      if (warehouseIdOf(rate) !== String(form.warehouse_id)) return false;
      return !toDayKey(rate.effective_to);
    });
    if (!openCard) return null;

    const existingFrom = toDayKey(openCard.effective_from);
    const existingEnd = "9999-12-31";
    const newEnd = to || "9999-12-31";
    const overlaps = from <= existingEnd && existingFrom <= newEnd;
    if (!overlaps || from <= existingFrom) return null;

    const dayBefore = addDays(from, -1);
    if (dayBefore < existingFrom) return null;

    const previousTo = openCard.effective_to ?? null;
    await warehouseCostRateService.edit(
      { effective_to: dayBefore },
      openCard._id,
    );
    return { id: openCard._id, previousTo };
  };

  const handleSave = async () => {
    if (!form.warehouse_id) {
      toast.error("Warehouse is required.");
      return;
    }
    if (!form.effective_from) {
      toast.error("Effective from is required.");
      return;
    }
    const payload = {
      client_id: clientId,
      warehouse_id: form.warehouse_id,
      currency: form.currency || "USD",
      effective_from: form.effective_from,
      effective_to: form.effective_to || null,
    };
    RATE_KEYS.forEach((key) => {
      payload[key] = form[key];
    });

    setSaving(true);
    let closed = null;
    try {
      closed = await closeOpenEndedCardIfNeeded();
      if (editing?._id) {
        await warehouseCostRateService.edit(payload, editing._id);
        toast.success("Cost rate updated.");
      } else {
        await warehouseCostRateService.create(payload);
        toast.success("Cost rate added.");
      }
      setModalOpen(false);
      await load();
    } catch (error) {
      if (closed?.id) {
        try {
          await warehouseCostRateService.edit(
            { effective_to: closed.previousTo },
            closed.id,
          );
        } catch (restoreError) {
          console.error("Failed to restore previous cost rate period:", restoreError);
        }
      }
      console.error("Save cost rate failed:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    try {
      await warehouseCostRateService.delete(deleteTarget._id);
      toast.success("Cost rate deleted.");
      setDeleteTarget(null);
      await load();
    } catch (error) {
      console.error("Delete cost rate failed:", error);
      setDeleteTarget(null);
    }
  };

  const addDisabled = disabled || warehouses.length === 0;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <TableContainer
        sx={{
          overflow: "auto",
          mt: 1,
          maxHeight: 500,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          border: "1px solid var(--border-color)",
          borderRadius: "8px",
        }}
        component={Paper}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Warehouse</TableCell>
              <TableCell sx={headerCellSx}>Inbound /ord</TableCell>
              <TableCell sx={headerCellSx}>Inbound /unit</TableCell>
              <TableCell sx={headerCellSx}>Storage /ord</TableCell>
              <TableCell sx={headerCellSx}>Storage /unit</TableCell>
              <TableCell sx={headerCellSx}>Outbound /ord</TableCell>
              <TableCell sx={headerCellSx}>Outbound /unit</TableCell>
              <TableCell sx={headerCellSx}>Currency</TableCell>
              <TableCell sx={headerCellSx}>From</TableCell>
              <TableCell sx={headerCellSx}>To</TableCell>
              <TableCell sx={headerCellSx}>
                <Button
                  disabled={addDisabled}
                  onClick={openCreate}
                  style={{
                    padding: 0,
                    marginRight: 8,
                    minHeight: 30,
                    border: addDisabled
                      ? "1px solid #BDBDBD"
                      : "1px solid var(--primary-blue)",
                  }}
                  variant="outlined"
                  startIcon={
                    <AddIcon
                      sx={{
                        color: addDisabled
                          ? "#BDBDBD"
                          : "var(--primary-blue)",
                      }}
                    />
                  }
                >
                  <Typography
                    fontSize="13px"
                    color={addDisabled ? "#BDBDBD" : "var(--primary-blue)"}
                    fontWeight={500}
                    fontFamily={"poppins"}
                  >
                    Rate
                  </Typography>
                </Button>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rates.map((rate, index) => (
              <TableRow
                key={rate._id}
                sx={{
                  backgroundColor:
                    index % 2 === 0
                      ? "var(--row-bg-odd)"
                      : "var(--row-bg-even)",
                }}
              >
                <TableCell>{warehouseLabel(rate, warehouses)}</TableCell>
                <TableCell>{formatRate(rate.inbound_fixed)}</TableCell>
                <TableCell>{formatRate(rate.inbound_per_unit)}</TableCell>
                <TableCell>{formatRate(rate.storage_fixed)}</TableCell>
                <TableCell>{formatRate(rate.storage_per_unit)}</TableCell>
                <TableCell>{formatRate(rate.outbound_fixed)}</TableCell>
                <TableCell>{formatRate(rate.outbound_per_unit)}</TableCell>
                <TableCell>{rate.currency || "USD"}</TableCell>
                <TableCell>{toDayKey(rate.effective_from) || "—"}</TableCell>
                <TableCell>
                  {toDayKey(rate.effective_to) || "Open"}
                </TableCell>
                <TableCell sx={{ paddingLeft: 6 }}>
                  <ActionTableCellComponent
                    selectedData={rate}
                    handleViewClick={(data) => openEdit(data, true)}
                    handleEditClick={(data) => openEdit(data, disabled)}
                    handleDeleteClick={(data) => {
                      if (!disabled) setDeleteTarget(data);
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && warehouses.length === 0 ? (
        <Box sx={{ mt: 1, px: 1 }}>
          <Typography fontFamily="Poppins" fontSize={13} color="var(--text-secondary)">
            Add a WMS warehouse first, then come back here to enter handling rates.
          </Typography>
        </Box>
      ) : null}
      {!loading && warehouses.length > 0 && rates.length === 0 ? (
        <Box sx={{ mt: 1, px: 1 }}>
          <Typography fontFamily="Poppins" fontSize={13} color="var(--text-secondary)">
            No cost rates yet. Add a dated card per warehouse (0 is allowed).
          </Typography>
        </Box>
      ) : null}

      <CostRateModal
        open={modalOpen}
        clientId={clientId}
        form={form}
        saving={saving}
        viewOnly={viewOnly}
        isEdit={Boolean(editing)}
        onChange={onChange}
        onWarehouseChange={onWarehouseChange}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />
      <DeleteConfirmationModal
        open={Boolean(deleteTarget)}
        handleClose={() => setDeleteTarget(null)}
        handleDelete={handleDelete}
        title={`Are you sure to delete this cost rate for ${warehouseLabel(
          deleteTarget,
          warehouses,
        )}`}
      />
    </div>
  );
};

export default CostRateList;
